/**
 * Weaving Raw Material Reconciliation (READ-ONLY).
 * Report 2 — shared RM store / weaving-consumed common RM (weft).
 * GET /api/weaving-reconciliation/raw-material. No WO selector. No writes.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CubeIcon, ArrowsClockwiseIcon, PrinterIcon, CaretRightIcon, CaretDownIcon } from "@phosphor-icons/react";

const kg = (n) => (n === null || n === undefined) ? "—" : `${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`;
const STATUS_CLS = (s) => {
  const u = (s || "").toUpperCase();
  if (u === "RECONCILED") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
  if (u === "SHORTAGE") return "bg-red-500/15 text-red-300 border-red-500/40";
  if (u === "EXCESS") return "bg-sky-500/15 text-sky-300 border-sky-500/40";
  if (u.includes("PENDING") || u.includes("PHY VER")) return "bg-amber-500/15 text-amber-300 border-amber-500/40";
  if (u === "VARIANCE") return "bg-orange-500/15 text-orange-300 border-orange-500/40";
  return "bg-zinc-500/15 text-zinc-300 border-zinc-500/40";
};
const Code = ({ children }) => <span className="font-mono text-[11px] text-white break-all whitespace-normal leading-snug">{children}</span>;
const Num = ({ children, cls = "" }) => <span className={`font-mono tabular-nums ${cls}`}>{children}</span>;
const Badge = ({ s }) => <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border whitespace-nowrap ${STATUS_CLS(s)}`}>{s}</span>;
const Section = ({ title, note, children, testId }) => (
  <div className="tactical-card overflow-hidden" data-testid={testId}>
    <div className="px-5 py-3 border-b border-[var(--border)] bg-[var(--bg-2)]/30">
      <div className="text-sm font-semibold text-white uppercase tracking-wide">{title}</div>
      {note && <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed max-w-4xl">{note}</div>}
    </div>
    <div className="overflow-x-auto">{children}</div>
  </div>
);
const Th = ({ children, r }) => <th className={`px-4 py-2 ${r ? "text-right" : "text-left"}`}>{children}</th>;

export default function WeavingRawMaterialReconciliation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rmFilter, setRmFilter] = useState("");
  const [open, setOpen] = useState({});

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/weaving-reconciliation/raw-material", { params: rmFilter ? { rm_item: rmFilter } : {} }); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load Weaving RM reconciliation"); setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const H = data?.header;
  const allCodes = (data?.section_a?.rows || []).map(r => r.rm_code);

  return (
    <div className="p-6 space-y-5" data-testid="weaving-rm-recon-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><CubeIcon size={11} weight="bold" />Reports · Reconciliation</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Weaving Raw Material Reconciliation</h1>
          <div className="text-xs text-zinc-500 mt-1">Shared RM store / weaving-consumed common RM (e.g. weft). Department-level, not WO-specific. SFW/SFSW are excluded (process-lineage SFG). Read-only.</div>
        </div>
        <div className="flex items-center gap-2">
          {data && <button onClick={() => window.print()} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2" data-testid="weav-rm-print"><PrinterIcon size={14} weight="bold" />Print</button>}
          <button onClick={load} disabled={loading} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2 disabled:opacity-40" data-testid="weav-rm-refresh"><ArrowsClockwiseIcon size={14} weight="bold" />Refresh</button>
        </div>
      </div>

      {H && (
        <div className="tactical-card p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px] font-mono" data-testid="weav-rm-header">
          <div><div className="text-zinc-500 mb-0.5 uppercase tracking-widest text-[9px]">Department</div><div className="text-white">{H.department}</div></div>
          <div><div className="text-zinc-500 mb-0.5 uppercase tracking-widest text-[9px]">Stock Scope</div><div className="text-amber-300 break-all">{H.stock_scope}</div></div>
          <div><div className="text-zinc-500 mb-0.5 uppercase tracking-widest text-[9px]">Store Location(s)</div><div className="text-white break-all">{(H.locations || []).join(", ") || "—"}</div></div>
          <div>
            <div className="text-zinc-500 mb-0.5 uppercase tracking-widest text-[9px]">Filter · Common RM Item</div>
            <select value={rmFilter} onChange={e => setRmFilter(e.target.value)} data-testid="weav-rm-filter" className="h-8 px-2 w-full bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-[11px]">
              <option value="">All Common RM</option>
              {allCodes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      )}

      {loading && <div className="tactical-card p-8 text-center text-zinc-500">Loading reconciliation…</div>}

      {data && (
        <>
          <Section title="A · Common RM Material Reconciliation" testId="weav-rm-section-a"
                   note="Calculated Book Balance = Store-Level Received − Total Weaving Consumption. Received is store-level (shared across processes), NOT a Weaving-only receipt. Expand a row for the Parent-WO consumption breakdown.">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]"><tr><th className="px-4 py-2 w-6"></th><Th>Common RM Item</Th><Th r>Store-Level Received</Th><Th r>Total Weaving Consumption</Th><Th r>Calculated Book Balance</Th><Th r>Stock V2</Th><Th r>Physical / Returned</Th><Th r>Variance</Th><th className="px-4 py-2 pl-6 text-left">Status</th></tr></thead>
              <tbody>
                {data.section_a.rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-500">No common RM consumed by Weaving</td></tr>}
                {data.section_a.rows.map((r, i) => (
                  <>
                    <tr key={i} className="border-b border-[var(--border)]/60 cursor-pointer hover:bg-[var(--bg-2)]/30 align-top" onClick={() => setOpen(o => ({ ...o, [r.rm_code]: !o[r.rm_code] }))} data-testid={`weav-rm-row-${i}`}>
                      <td className="px-4 py-2 text-zinc-400">{(r.drill_down || []).length > 0 ? (open[r.rm_code] ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />) : null}</td>
                      <td className="px-4 py-2 max-w-[360px]"><Code>{r.rm_code}</Code></td>
                      <td className="px-4 py-2 text-right"><Num cls="text-emerald-300">{kg(r.store_received)}</Num></td>
                      <td className="px-4 py-2 text-right"><Num cls="text-purple-300">{kg(r.total_consumption)}</Num></td>
                      <td className="px-4 py-2 text-right"><Num cls="text-amber-300">{kg(r.book_balance)}</Num></td>
                      <td className="px-4 py-2 text-right"><Num cls="text-sky-300">{kg(r.v2_balance)}</Num></td>
                      <td className="px-4 py-2 text-right text-zinc-500">{r.physical_returned === null ? "—" : kg(r.physical_returned)}</td>
                      <td className="px-4 py-2 text-right text-zinc-500">{r.variance === null ? "—" : kg(r.variance)}</td>
                      <td className="px-4 py-2 pl-6"><Badge s={r.status} /></td>
                    </tr>
                    {open[r.rm_code] && (
                      <tr className="bg-[var(--bg-0)]/40 border-b border-[var(--border)]/30" data-testid={`weav-rm-drill-${i}`}>
                        <td></td>
                        <td colSpan={8} className="px-4 py-2">
                          <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Parent-WO Consumption Breakdown (common pool · cross-WO permitted)</div>
                          <table className="w-full text-[11px]">
                            <thead className="text-zinc-500"><tr><th className="text-left py-1">Parent WO</th><th className="text-left py-1">SO / ESO</th><th className="text-left py-1">Product</th><th className="text-left py-1">Batch / Lot</th><th className="text-left py-1">Posting Ref</th><th className="text-right py-1">Consumed</th></tr></thead>
                            <tbody>
                              {r.drill_down.map((d, j) => (
                                <tr key={j} className="border-t border-[var(--border)]/30">
                                  <td className="py-1 font-mono text-white">{d.parent_wo_no}</td>
                                  <td className="py-1 font-mono text-zinc-300">{d.so_eso_no || "—"}</td>
                                  <td className="py-1 font-mono text-zinc-300 break-all max-w-[220px]">{d.product_code || "—"}</td>
                                  <td className="py-1 font-mono text-sky-300 break-all">{d.batch_lot}</td>
                                  <td className="py-1 font-mono text-zinc-400">{d.posting_ref || "—"}</td>
                                  <td className="py-1 text-right"><Num>{kg(d.consumed)}</Num></td>
                                </tr>
                              ))}
                              <tr className="border-t border-[var(--border)] font-semibold text-white"><td className="py-1" colSpan={5}>TOTAL</td><td className="py-1 text-right"><Num>{kg(r.total_consumption)}</Num></td></tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="B · Reconciliation Control & Exceptions" testId="weav-rm-section-b"
                   note="All applicable control rows (including RECONCILED). A WO Material Accountability exception in the Production report does NOT create a physical/common-stock variance here — these are independent controls.">
            <table className="w-full text-sm table-fixed">
              <colgroup><col style={{ width: "40%" }} /><col style={{ width: "23%" }} /><col style={{ width: "8%" }} /><col style={{ width: "8%" }} /><col style={{ width: "8%" }} /><col style={{ width: "13%" }} /></colgroup>
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]"><tr><Th>Item</Th><Th>Control Type</Th><Th r>Expected</Th><Th r>Actual</Th><Th r>Variance</Th><Th>Status</Th></tr></thead>
              <tbody>
                {data.section_b.rows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-zinc-500">No control rows</td></tr>}
                {data.section_b.rows.map((r, i) => (
                  <tr key={i} className="border-b border-[var(--border)]/50 align-top" data-testid={`weav-rm-ctrl-${i}`}>
                    <td className="px-4 py-2"><Code>{r.item}</Code></td>
                    <td className="px-4 py-2"><span className="text-[11px] text-zinc-300 break-words leading-snug">{r.control}</span></td>
                    <td className="px-4 py-2 text-right text-zinc-400">{r.expected === null ? "—" : kg(r.expected)}</td>
                    <td className="px-4 py-2 text-right text-zinc-400">{r.actual === null ? "—" : kg(r.actual)}</td>
                    <td className="px-4 py-2 text-right text-zinc-400">{r.variance === null ? "—" : kg(r.variance)}</td>
                    <td className="px-4 py-2"><Badge s={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </div>
  );
}
